/**
 * shared/contexts/ToastContext.js
 * Shared toast notification context.
 * Identical logic to src/context/ToastContext.js — import paths adjusted.
 */
import { createContext, useContext, useState, useCallback } from "react";
import theme from "../theme";

const ToastContext = createContext(null);

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
}

const typeStyles = {
  success: { bg: "#047857", mark: "" },
  error:   { bg: "#B91C1C", mark: "" },
  info:    { bg: theme.colors.primary, mark: "" },
  warning: { bg: "#B45309", mark: "" },
};

function ToastContainer({ toasts }) {
  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: "fixed",
      bottom: 24,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 99999,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      width: "min(360px, calc(100vw - 32px))",
      pointerEvents: "none",
    }}>
      {toasts.map((t) => {
        const { bg, mark } = typeStyles[t.type] || typeStyles.info;
        return (
          <div
            key={t.id}
            className="fade-in"
            style={{
              background: bg,
              color: "white",
              padding: "12px 14px",
              borderRadius: theme.radius.md,
              boxShadow: "0 8px 24px rgba(15,23,42,0.24)",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: theme.font.family,
              border: "1px solid rgba(255,255,255,0.16)",
            }}
          >
            <span style={{
              width: 8,
              height: 8,
              borderRadius: 99,
              background: "rgba(255,255,255,0.9)",
              flexShrink: 0,
            }}>{mark}</span>
            <span style={{ lineHeight: 1.4 }}>{t.message}</span>
          </div>
        );
      })}
    </div>
  );
}
